// Third party imports
import { useQuery } from "@tanstack/react-query";
import {
    UsStockListApiResponse,
    UsStockDetailsApiResponse,
    NewsAndEventsResponse,
    SimilarStocksResponse,
    ProductPriceResponse,
} from "@/app/interface/global";
import { generalProdctType } from "@/types/product";
import { fetchProductCategoryList, fetchProductsByCategory } from "@/app/global/actions";

const target = process.env.APP_NODE_ENV !== "uat" ? "investors" : "investors"; /* "partners" */

export const MIN_US_STOCK_SEARCH_LENGTH = 1;

export function useUsStocksListingQuery() {
    const { data: usStockList, isLoading: isUsStockListLoading } = useQuery({
        queryKey: ["us-stocks-list"],
        queryFn: async (): Promise<UsStockListApiResponse> => {
            const url = `${process.env.API_GLOBAL_US_EQITIES}/v1/global-stocks/${target}/market-watch/stock-list?page=1&per_page=100&exchange=US`;

            try {
                const response = await fetch(url);

                if (response) {
                    return response.json();
                } else {
                    return null;
                }
            } catch (ex: unknown) {
                console.log("error: error occured while fetching US Stocks listing api", ex);
                return null;
            }
        },
        refetchOnWindowFocus: false,
        // enabled: true,
    });

    return {
        usStockList,
        isUsStockListLoading,
    };
}

type UseUsStockSearchQueryParams = {
    query: string;
    type?: string;
    page?: number;
    perPage?: number;
};

export function useUsStockSearchQuery({
    query,
    type = generalProdctType.STOCK,
    page = 1,
    perPage = 20,
}: UseUsStockSearchQueryParams) {
    const { data: usStockSearchResult, isLoading: isUsStockSearchLoading } = useQuery({
        queryKey: ["us-stock-search", query, type],
        queryFn: async (): Promise<UsStockListApiResponse> => {
            const url = `${process.env.API_GLOBAL_US_EQITIES}/v1/global-stocks/${target}/market-watch/stock-list?q=${encodeURIComponent(query)}&page=${page}&type=${type}&sort=-1&exchange=US&per_page=${perPage}`;

            try {
                const response = await fetch(url);

                if (response) {
                    const result = await response.json();
                    return result?.data ?? null;
                } else {
                    return null;
                }
            } catch (ex: unknown) {
                console.log("error: error occured while fetching global stocks search api", ex);
                return null;
            }
        },
        refetchOnWindowFocus: false,
        enabled: query.trim().length >= MIN_US_STOCK_SEARCH_LENGTH && Boolean(type),
    });

    return {
        usStockSearchResult,
        isUsStockSearchLoading,
    };
}

type UseUsStockQueryParams = {
    symbol: string;
};

export function useUsStockQuery({ symbol }: UseUsStockQueryParams) {
    const { data: usStockDetail, isLoading: usStockDetailLoading } = useQuery({
        queryKey: ["us-stock-detail"],
        queryFn: async (): Promise<UsStockDetailsApiResponse> => {
            const url = `${process.env.API_GLOBAL_US_EQITIES}/v1/global-stocks/${target}/market-watch/stock-details?symbol=${symbol}`;

            try {
                const response = await fetch(url);

                if (response) {
                    return response.json();
                } else {
                    return null;
                }
            } catch (ex: unknown) {
                console.log("error: error occured while fetching US Stocks detail api", ex);
                return null;
            }
        },
        refetchOnWindowFocus: false,
        // enabled: true,
    });

    return {
        usStockDetail,
        usStockDetailLoading,
    };
}

export function useProductCategoryList() {
    const { data: productCategoryList, isLoading: isProductCategoryListLoading } = useQuery({
        queryKey: ["product-category-list"],
        queryFn: fetchProductCategoryList,
        refetchOnWindowFocus: false,
        // enabled: true,
    });

    return {
        productCategoryList,
        isProductCategoryListLoading,
    };
}

type useProductsByCategoryQueryProps = {
    categoryIndex: number;
};

export function useProductsByCategoryQuery({ categoryIndex }: useProductsByCategoryQueryProps) {
    const { data: productsByCategoryData, isLoading: isProductsByCategoryDataLoading } = useQuery({
        queryKey: ["products-by-category", categoryIndex],
        queryFn: () => fetchProductsByCategory(categoryIndex),
        refetchOnWindowFocus: false,
        enabled: Boolean(categoryIndex) || categoryIndex === 0,
    });

    return {
        productsByCategoryData,
        isProductsByCategoryDataLoading,
    };
}

export type GraphOhlc = {
    t: number;
    o: number;
    h: number;
    l: number;
    c: number;
    v: string;
};

export type UsStockChartResponse = {
    data: {
        graph_ohlc: GraphOhlc[];
    };
};

export type UsStockChartProps = {
    symbol: string;
    from: string;
    to: string;
    resolution: string;
};

export function useUsStockChartQuery({ symbol, from, to, resolution }: UsStockChartProps) {
    const { data: usStockChart, isLoading: isUsStockChartLoading } = useQuery({
        queryKey: ["us-stock-chart", symbol, from, to, resolution],
        queryFn: async (): Promise<UsStockChartResponse | null> => {
            const url = `${process.env.API_GLOBAL_US_EQITIES}/v1/global-stocks/${target}/market-watch/stock-graph?symbol=${symbol}&from=${from}&to=${to}&resolution=${resolution}`;

            try {
                const response = await fetch(url, {
                    method: "GET",
                });

                if (response) {
                    return response.json();
                } else {
                    return null;
                }
            } catch (ex: unknown) {
                console.log("error: Error in US Stock chart api", ex);
                return null;
            }
        },
        refetchOnWindowFocus: false,
        enabled: Boolean(symbol && from && to && resolution),
    });

    return {
        usStockChart,
        isUsStockChartLoading,
    };
}

type NewsAndEventsProps = {
    symbol: string;
    from?: string;
    to?: string;
};

export function useNewsAndEventsQuery({ symbol, from, to }: NewsAndEventsProps) {
    const { data: newsAndEvents, isLoading: isNewsAndEventsLoading } = useQuery({
        queryKey: ["us-stock-news", symbol, from, to],
        queryFn: async (): Promise<NewsAndEventsResponse | undefined> => {
            const url = `${process.env.API_GLOBAL_US_EQITIES}/v1/global-stocks/${target}/market-watch/stock-news?symbol=${symbol}`; /* &from=${from}&to=${to}  &page=1&per_page=20&lang=en */

            try {
                const response = await fetch(url, {
                    method: "GET",
                });

                if (response) {
                    return response.json();
                } else {
                    return undefined;
                }
            } catch (ex: unknown) {
                console.log("error: Unable to fetch US Stock news", ex);
                return undefined;
            }
        },
        refetchOnWindowFocus: false,
        enabled: Boolean(symbol && from && to),
    });

    return {
        newsAndEvents,
        isNewsAndEventsLoading,
    };
}

type SimilarStocksProps = {
    symbol: string;
};

export function useSimilarStocksQuery({ symbol }: SimilarStocksProps) {
    const { data: similarStocks, isLoading: isSimilarStocksLoading } = useQuery({
        queryKey: ["us-stock-similar-stocks", symbol],
        queryFn: async (): Promise<SimilarStocksResponse | undefined> => {
            const url = `${process.env.API_GLOBAL_US_EQITIES}/v1/global-stocks/${target}/market-watch/similar-stocks/${symbol}`;

            try {
                const response = await fetch(url, {
                    method: "GET",
                });

                if (response) {
                    return response.json();
                } else {
                    return undefined;
                }
            } catch (ex: unknown) {
                console.log("error: Unable to fetch US Stock news", ex);
                return undefined;
            }
        },
        refetchOnWindowFocus: false,
        enabled: Boolean(symbol),
    });

    return {
        similarStocks,
        isSimilarStocksLoading,
    };
}

type ProductPriceProps = {
    symbol: string;
};

export function useProductPriceQuery({ symbol }: ProductPriceProps) {
    const { data: productPrice, isLoading: isproductPriceLoading } = useQuery({
        queryKey: ["us-stock-product-price", symbol],
        queryFn: async (): Promise<ProductPriceResponse | undefined> => {
            const url = `${process.env.API_GLOBAL_US_EQITIES}/v1/global-stocks/${target}/market-watch/stock-price?symbol=${symbol}`;

            try {
                const response = await fetch(url, {
                    method: "GET",
                });

                if (response) {
                    return response.json();
                } else {
                    return undefined;
                }
            } catch (ex: unknown) {
                console.log("error: Unable to fetch US Stock price", ex);
                return undefined;
            }
        },
        refetchOnWindowFocus: false,
        enabled: Boolean(symbol),
    });

    return {
        productPrice,
        isproductPriceLoading,
    };
}
